import React, { useRef, useState, useEffect, useMemo } from "react";
import { db, auth } from "../services/firebase";
import firebase from 'firebase/app';
import 'firebase/firestore';

import { useCollectionData, useDocumentData } from 'react-firebase-hooks/firestore';

import { Icon, Button, Badge } from 'react-materialize';

import '../css/realtimechat.css';

import { inverseColorHSL, randomColorHSL } from "../helpers/Types.js";

function getMyMessages() {
  let myMessages = sessionStorage.getItem("myMessages");
  if (myMessages) {
    return JSON.parse(myMessages);
  } else {
    return [];
  }
}

function addToMyMessages(toAdd) {
  let myMessages = getMyMessages();
  myMessages.push(toAdd);
  sessionStorage.setItem("myMessages", JSON.stringify(myMessages));
}

export default function RealtimeChatAdmin(props) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isShowingAll, setIsShowingAll] = useState(false);
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(false);
  const [isShowingDeleted, setIsShowingDeleted] = useState(false);

  useEffect(() => {
    if (auth().currentUser && !auth().currentUser.isAnonymous) {
      db.collection('users').doc(auth().currentUser.uid).get().then(user => {
        if (user.exists && user.data().type === "admin") {
          setIsAdmin(true);
        }
      }).catch((error) => {
        console.log("Error getting currentUser:", error);
      });
    }
  }, []);

  const dummy = useRef();
  const color = useRef("#0df233");
  const colorAI = useRef("#f20df1");

  let query = db.collection("realtime");
  if (!isShowingAll) {
    query = query.where('wasShown', '==', false)
      .where('isShowing', '==', true);
  }
  if (isShowingAll && !isShowingDeleted) {
    query = query.where('isDeleted', '==', false);
  }
  query = query.orderBy('timestamp');

  let waitingQuery = db.collection("realtime")
    .where('wasShown', '==', false)
    .where('isShowing', '==', false);

  const [messages, loadingMessages] = useCollectionData(query);
  const [waiting, loadingWaiting] = useCollectionData(waitingQuery);
  let paramsQuery = db.collection("params").doc('realtime');
  const [params, loadingParams] = useDocumentData(paramsQuery);

  const [formValue, setFormValue] = useState('');
  const [usernameValue, setUsernameValue] = useState('');
  const [waitingMessage, setWaitingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);

  // --- Regroupement messages et leurs replies ---
  // Mapping parentId ("" pour parent) => array de messages
  const messagesByParentId = useMemo(() => {
    const map = {};
    if (!messages) return map;
    messages.forEach((msg) => {
      const parentId = msg.replyingTo || "";
      if (!map[parentId]) {
        map[parentId] = [];
      }
      map[parentId].push(msg);
    });
    return map;
  }, [messages]);

  // Gestion de la soumission du message
  const sendMessage = async (e) => {
    e.preventDefault();

    let docRef = await db.collection("realtime").add({
      Body: formValue,
      ProfileName: usernameValue,
      timestamp: firebase.firestore.Timestamp.now(),
      wasShown: false,
      isShowing: false,
      isDeleted: false,
      isRealTime: true,
      replyingTo: (replyingTo ? replyingTo.id : ""),
    });

    await db.collection("realtime").doc(docRef.id).update({
      id: docRef.id,
    });

    addToMyMessages(docRef.id);

    setWaitingMessage({
      id: docRef.id,
      body: formValue,
      name: usernameValue,
    });

    setFormValue('');
    setUsernameValue('');
    setReplyingTo(null);

    // Scroll vers le bas
    dummy.current.scrollIntoView({ behavior: 'smooth' });
  };

  let myMessages = getMyMessages();

  // Clear waitingMessage une fois message visible dans messages
  useEffect(() => {
    if (waitingMessage && !loadingMessages) {
      for (let i = 0; i < messages.length; i++) {
        if (messages[i].id === waitingMessage.id) {
          setWaitingMessage(null);
          break;
        }
      }
    }
  }, [messages, loadingMessages, waitingMessage]);

  // Callbacks pour admin controls
  const showAllMessagesCallback = () => {
    if (isAdmin) setIsShowingAll(!isShowingAll);
  };

  const enableDeleteCallback = () => {
    if (isAdmin) setIsDeleteEnabled(!isDeleteEnabled);
  };

  const replyCallback = (msg) => {
    setReplyingTo(msg);
  };

  // Delete message (soft delete)
  async function deleteCallback(id) {
    try {
      // Marquer d'abord le message parent comme supprimé
      await db.collection("realtime").doc(id).update({
        isDeleted: true,
      });

      // Chercher toutes les réponses à ce message
      const repliesSnapshot = await db.collection("realtime")
        .where("replyingTo", "==", id)
        .get();

      // Supprimer (soft delete) toutes les réponses
      const batch = db.batch();
      repliesSnapshot.forEach((doc) => {
        batch.update(doc.ref, { isDeleted: true });
      });
      await batch.commit();

      console.log("Message and its replies deleted!", id);
    } catch (error) {
      console.error("Error deleting message and replies: ", error);
    }
  }

  // --- Composant récursif pour afficher message et ses replies ---
  function RenderMessageWithReplies({
    message,
    level = 0
  }) {
    const replies = messagesByParentId[message.id] || [];
    // const indentStyle = {
    //   marginLeft: level * 20 + "px",
    //   borderLeft: level > 0 ? "2px solid #ccc" : "none",
    //   paddingLeft: level > 0 ? "8px" : "0",
    // };

    return (
      <React.Fragment key={message.id}>
        <div>
          <RenderMessage
            className={level > 0 ? "replyChatMessage" : ""}
            color={level > 0 ? colorAI.current : color.current}
            message={message}
            myMessages={myMessages}
            isAdmin={isAdmin}
            isDeleteEnabled={isDeleteEnabled}
            isShowingAll={isShowingAll}
            isShowingDeleted={isShowingDeleted}
            replyCallback={replyCallback}
            deleteCallback={deleteCallback}
          />
        </div>
        {replies.map(reply => (
          <RenderMessageWithReplies
            key={reply.id}
            message={reply}
            level={level + 1}
            color={color.current}
          />
        ))}
      </React.Fragment>
    );
  }

  return (
    <>
      <div className="realtimeContainer">
        {isAdmin && (
          <RenderAdminBar
            showAllMessagesCallback={showAllMessagesCallback}
            isShowingAll={isShowingAll}
            isDeleteEnabled={isDeleteEnabled}
            enableDeleteCallback={enableDeleteCallback}
            isShowingDeleted={isShowingDeleted}
            setIsShowingDeleted={setIsShowingDeleted}
          />
        )}
        <div className="realtimeMessagesContainer">
          {loadingMessages && <div>loading...</div>}
          <ul>
            {!loadingMessages && messages && messagesByParentId[""] && messagesByParentId[""].map((msg) => (
              <RenderMessageWithReplies
                key={msg.id}
                message={msg}
              />
            ))}
          </ul>
          <span ref={dummy}></span>
        </div>

        <div className="realtimeInputContainer" style={{ backgroundColor: color.current }}>
          {replyingTo && (
            <RenderMessageReply message={replyingTo} replyCallback={replyCallback} />
          )}

          {waitingMessage && !loadingMessages &&
            <RenderWaitingMessage
              waitingMessage={waitingMessage}
              params={params}
              loadingParams={loadingParams}
              waiting={waiting}
              loadingWaiting={loadingWaiting}
            />
          }

          <form className="realtimeInputForm" onSubmit={sendMessage}>

            <div className="realtimeInputs">
              <input
                id="realtimeInputName"
                className="realtimeInput"
                value={usernameValue}
                onChange={(e) => setUsernameValue(e.target.value)}
                placeholder="Your Name"
              />
              <input
                id="realtimeInputText"
                className="realtimeInput"
                value={formValue}
                onChange={(e) => setFormValue(e.target.value)}
                placeholder="Message"
              />
            </div>

            <button
              className="realtimeSend"
              type="submit"
              disabled={!formValue || !usernameValue || waitingMessage}
            >
              <Icon>send</Icon>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

function RenderWaitingMessage(props) {
  return (
    <>
      {(!props.loadingParams && !props.loadingWaiting && props.params) && (
        <div className="realtimeCard-waitTime white-text">
          {"Your message has been received!"}
        </div>
      )}
    </>
  );
}

function RenderAdminBar(props) {
  return (
    <>
      <div className="realtimeAdminTopBar">

        {props.isDeleteEnabled && (
          <Button
            className="yellow black-text right adminShowAllMessagesButton"
            node="button"
            small
            tooltip={props.isShowingDeleted ? "Hide deleted messages" : "Show deleted messages"}
            waves="light"
            onClick={() => props.setIsShowingDeleted(!props.isShowingDeleted)}
          >
            {props.isShowingDeleted ? "Hide deleted" : "Show deleted"}
          </Button>
        )}

        <Button
          className="red right adminShowAllMessagesButton"
          node="button"
          small
          tooltip={props.isDeleteEnabled ? "Disable delete" : "Enable delete"}
          waves="light"
          onClick={() => props.enableDeleteCallback()}
        >
          {props.isDeleteEnabled ? "Disable delete" : "Enable delete"}
        </Button>

        <Button
          className="teal right adminShowAllMessagesButton"
          node="button"
          small
          tooltip="Show all messages"
          waves="light"
          onClick={() => props.showAllMessagesCallback()}
        >
          {props.isShowingAll ? "Only show realtime messages" : "Show all Messages"}
        </Button>
      </div>
    </>
  );
}

function RenderMessageReply(props) {
  let messageClass = "z-depth-0  card realtimeMessage replyChatMessage ";
  if (Array.isArray(props.myMessages) && props.myMessages.includes(props.message.id)) {
    messageClass += "ownReplyChatMessage";
  }
  return (
    <>
      {props.message && (
        <li id={"realtimemessagereply-" + props.message.id} className={messageClass}>
          <div className="realtimeReplyCard-header valign-wrapper" style={{color: props.color}}>
            {"Reply to: " + props.message.ProfileName}
          </div>
          <Button
            className="btn-floating  grey right replyMessageButton"
            node="button"
            small
            tooltip="Cancel reply"
            waves="light"
            floating
            icon={<Icon>cancel</Icon>}
            onClick={() => props.replyCallback(null)}
          />
          <div className="realtimeReplyCard-content white-text">
            {props.message.Body}
          </div>
        </li>
      )}
    </>
  );
}

function RenderMessage(props) {
  let messageClass = "z-depth-0  card realtimeMessage " + props.className;
  if (Array.isArray(props.myMessages) && props.myMessages.includes(props.message.id)) {
    messageClass += "ownChatMessage";
  }

  let badgeClass = "realtimeMessageBadge " + (props.message.wasShown ? "blue white-text" : "yellow black-text");

  let classsRTMsg = "btn-floating  grey right ";
  if (props.isAdmin && props.isDeleteEnabled && !props.message.isDeleted) {
    classsRTMsg += "replyMessageButtonAdminWithDelete";
  } else {
    classsRTMsg += "replyMessageButtonAdmin ";
  }
  return (
    <>
      <li id={"realtimemessage-" + props.message.id} className={messageClass}>
        <div className="realtimeCard-header valign-wrapper">
          {props.isAdmin && props.isDeleteEnabled && !props.message.isDeleted && (
            <Button
              className="red right removeImageButton"
              node="button"
              small
              tooltip="Delete this message"
              waves="light"
              floating
              icon={<Icon>delete</Icon>}
              onClick={() => props.deleteCallback(props.message.id)}
            />
          )}
          {props.isAdmin && props.isShowingAll && (
            <div>
              {!props.message.isDeleted && <Badge className={badgeClass}>{"was shown " + (props.message.wasShown ? "YES" : "NO")}</Badge>}
              {props.message.isShowing && !props.message.isDeleted && <Badge className="green white-text realtimeMessageBadge">{"is showing now"}</Badge>}
              {props.message.isDeleted && <Badge className="red white-text realtimeMessageBadge">{"DELETED"}</Badge>}
            </div>
          )}
          <span style={{color: props.color}}>
            {props.message.ProfileName}
          </span>
        </div>
        <Button
          className={classsRTMsg}
          node="button"
          small
          tooltip="Reply this message"
          waves="light"
          floating
          icon={<Icon>reply</Icon>}
          onClick={() => props.replyCallback(props.message)}
        />
        <div className="realtimeCard-content white-text">
          {props.message.Body}
        </div>
      </li>
    </>
  );
}
